define((require) => {
    "use strict";

    const {extend, result}         = require("underscore");
    const DropAreaHotspot          = require("ui/behaviors/DropAreaHotspot");
    const ImageCollectionView      = require("ui/common/ImageCollectionView");
    const ContentQuery             = require("data/ContentQuery");
    const EditorEvents             = require("shared/services/EditorEvents");
    const ImageLibrarySourceEnum   = require("shared/data/images/ImageLibrarySourceEnum");
    const ImageLibrarySortingEnum  = require("ui/imageLibrary/ImageLibrarySortingEnum");
    const ImprovedView             = require("editor/zbone/ImprovedView");
    const Syncer                   = require("shared/services/sync/Syncer");
    const template                 = require("ui/imageLibrary/ImageLibraryCollectionView.tpl");
    const SortingPanel             = require("ui/common/SortingPanel");

    return ImprovedView.extend("ImageLibraryView", {

        className: "image-library",

        template,

        horizontalScrollBuffer: 220,

        /**
         * Options passed to the <tt>SortingPanel</tt>.
         *
         * @type {Object}
         */
        sortingPanelOptions: {
            project: null,
            showSources: false
        },

        collectionViewOptions: {},

        sourcePanel: null,
        sortingPanel: null,

        regions: {
            sortingPanelRegion: ".sorting-panel-region",
            content: ".content"
        },

        behaviors: {
            dropAreaHotspot: {
                behaviorClass: DropAreaHotspot,
                message: "<h4>Add <br/>images to Library </h4><p>Drag from desktop or click to upload</p>",
                dropAreaSelector: ".collection",
                iconSize: "small"
            }
        },

        initialize() {
            this._imageLibrarySorting = this.options.imageLibrarySorting;
            this.collection = this.options.collection;
        },

        onShow() {
            const imageCollectionViewOptions = extend({
                collection: this.collection,
                itemView: this.itemView || this.options.itemView,
                itemViewOptions: this.options.itemViewOptions,
                horizontalBuffer: this.horizontalScrollBuffer,
                scrollTarget: this._determineScrollTarget()
            }, this.options.collectionViewOptions || this.collectionViewOptions);

            this.sortingPanel = new SortingPanel(extend(this.sortingPanelOptions, {
                sorting: this._imageLibrarySorting,
                sourceEnum: ImageLibrarySourceEnum
            }));

            this._collectionView = new ImageCollectionView(imageCollectionViewOptions);
            this.content.show(this._collectionView);

            this.listenTo(this.collection, {
                "sync:state": () => { // only used for fetch
                    this.sortingPanel.disable(!Syncer.State.complete(this.collection));
                }
            });

            this.listenTo(this.sortingPanel, {
                "sort": (sorting) => {
                    this.collection.query(sorting.query);
                },

                "search": (tags, sorting, source) => {
                    // Searching tag names are only supported for Creator images
                    const includeTagName = source === ImageLibrarySourceEnum.CREATOR;
                    this.collection.query(ContentQuery.search(tags, sorting.query, includeTagName));
                },

                "source:changed": (source, options = {}) => {
                    const isCreator = source === ImageLibrarySourceEnum.CREATOR;
                    this.collection.updateSource(source);

                    if (this.options.project && isCreator) {
                        this.collection.options.project = this.options.project;
                    } else {
                        delete this.collection.options.project;
                    }

                    this.collection.query(this.collection.currentQuery());
                    this.sortingPanel.toggleFiltering(Boolean(this.sortingPanelOptions.project) && isCreator);

                    // There are cases where events shouldn't trigger source change stats event
                    // e.g. on first click on an image
                    if (!options.ignoreEditorEvent && this.viewScope.viewModel) {
                        const eventProperties = {
                            triggerOrigin: "ImageLibrary",
                            sourceName: source.name
                        };

                        const experience = this.viewScope.viewModel.get("experience");

                        if (experience) {
                            eventProperties.experienceId = experience.get("id");
                            eventProperties.experienceVersion = experience.get("version");
                            eventProperties.experiencePushedVersion = experience.get("pushedVersion");

                            if (this.sortingPanelOptions.project) {
                                eventProperties.projectId = this.sortingPanelOptions.project.get("id");
                            }
                        }
                        // Make sure we log the event
                        EditorEvents.trigger(EditorEvents.constants.SELECT_SOURCE, eventProperties);
                    }
                }
            });

            if (this.options.project) {
                this.listenTo(this.sortingPanel, "select", (text) => {
                    if (text === SortingPanel.FILTER_ATTRIBUTES.project) {
                        this.collection.options.project = this.options.project;
                    } else {
                        delete this.collection.options.project;
                    }

                    this.collection.query(this.collection.currentQuery());
                });
            }

            this.sortingPanelRegion.show(this.sortingPanel);

            // Delegate "load" events (picked up by the DropAreaHotspot)
            this.listenTo(this._collectionView, {
                "load:start": this.triggerMethod.bind(this, "load:start"),
                "load:end": this.triggerMethod.bind(this, "load:end")
            });

            // Set the event listener for upload as the triggerMethod approach
            // does not properly remove the eventlistener
            this.listenTo(this, "upload", this._onUpload);
        },

        refresh(sorting) {
            this.sortingPanel.setSorting(sorting || this.sortingPanel.getSorting());
        },

        active(active) {
            this._collectionView.active(active);
        },

        /**
         * Invoked when uploading (triggered by the <tt>DropAreaHotspot</tt>).
         *
         * @param  {{promise: Promise, models: ImageModel[], descriptors: Object[]}} descriptor The descriptor object created by the <tt>ImageDropManager</tt>.
         *
         * @see ImageDropManager
         */
        _onUpload({promise, descriptors, models}) {
            const changeSource = this._imageLibrarySorting.getSelectedSource() !== ImageLibrarySourceEnum.CREATOR;

            // Upload can only happen where Creator is the image source
            if (changeSource) {
                this.sortingPanel.setSource(ImageLibrarySourceEnum.CREATOR);
            }

            promise
                .then(() => {
                    // Avoid re-render if sorting is already set to last uploaded
                    if (!changeSource && this._imageLibrarySorting.getSelectedSorting() !== ImageLibrarySortingEnum.LAST_UPLOADED) {
                        this.sortingPanel.setSorting(ImageLibrarySortingEnum.LAST_UPLOADED);
                    }
                })
                .always(() => {
                    this.sortingPanel.disable(false);
                });

            // Handle failing upload - make sure to remove the model from the collection.
            descriptors.forEach((imageUploadDescriptor) => {
                imageUploadDescriptor.promise.fail(() => {
                    this.collection.remove(imageUploadDescriptor.model);
                });
            });

            if (models.length > 0) {
                this._trackEditorEvent(models);
            }

            this.sortingPanel.disable();
            this.collection.add(models, 0);
        },

        _trackEditorEvent(imageModels) {
            const viewModel = this.viewScope.viewModel;
            const project = this.options.project;

            const imageNames = imageModels.map((image) => {
                return image.get("name");
            });
            const eventProperties = {
                numberOfUploadedImages: imageModels.length,
                imageNames: imageNames.join(),
                projectId: project && project.get("id")
            };

            // Means we are in the context of an experience
            if (viewModel) {
                const experience = viewModel.get("experience");
                eventProperties.location = "edit";
                eventProperties.experienceId = experience.get("id");
                eventProperties.experienceVersion = experience.get("version");
                eventProperties.experiencePushedVersion = experience.get("pushedVersion");
                eventProperties.triggerOrigin = "ImageLibraryEditor";
            } else {
                eventProperties.triggerOrigin = "ImageLibraryTab";
            }

            EditorEvents.trigger(EditorEvents.constants.UPLOAD_IMAGE, eventProperties);
        },

        _determineScrollTarget() {
            const scrollTarget = result(this.options, "scrollTarget") || result(this.viewScope, "scrollTarget");

            return scrollTarget || this.$(".content");
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/imageLibrary/ImageLibraryCollectionView.js